/* Global: money format, mobile menu, cart drawer, FAQ accordion. Vanilla JS, no dependencies. */
(() => {
  const theme = (window.theme = window.theme || {});

  theme.formatMoney = (cents, format) => {
    const fmt = format || theme.moneyFormat || '${{amount}}';
    const value = (Number(cents) || 0) / 100;
    const group = (n, decimals, thousands, dec) => {
      const [int, frac] = n.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? dec + frac : '');
    };
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    let out;
    switch (match && match[1]) {
      case 'amount_no_decimals':
        out = group(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = group(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = group(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = group(value, 2, "'", '.');
        break;
      default:
        out = group(value, 2, ',', '.');
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
    return tmp.textContent;
  };

  /* Mobile menu */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-menu-toggle]');
    if (!btn) return;
    const nav = document.getElementById(btn.getAttribute('aria-controls'));
    const open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', open);
    if (nav) nav.hidden = !open;
  });

  /* FAQ: one open at a time (fallback for browsers without <details name>) */
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open || !d.name) return;
      document.querySelectorAll(`details[name="${CSS.escape(d.name)}"]`).forEach((o) => {
        if (o !== d) o.open = false;
      });
    },
    true
  );

  /* Cart drawer */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) return this.close();
        const q = e.target.closest('[data-qty]');
        if (q) {
          const line = q.closest('[data-line]');
          this.change(line.dataset.line, +q.dataset.qty);
        }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
      if (theme.cartDrawer) {
        document.addEventListener('click', (e) => {
          const opener = e.target.closest('[data-cart-open]');
          if (!opener || document.body.classList.contains('template-cart')) return;
          e.preventDefault();
          this.open();
        });
      }
    }

    open() {
      this.lastFocus = document.activeElement;
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      document.body.classList.add('drawer-open');
      this.panel.focus();
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      setTimeout(() => {
        this.hidden = true;
      }, 250);
      if (this.lastFocus) this.lastFocus.focus();
    }

    renderSections(sections, open) {
      const html = sections && sections['cart-drawer'];
      if (!html) {
        window.location.href = theme.routes.cart;
        return;
      }
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
      if (!fresh) return;
      this.panel.innerHTML = fresh.querySelector('.drawer__panel').innerHTML;
      const count = fresh.dataset.count;
      this.dataset.count = count;
      document.querySelectorAll('[data-cart-count]').forEach((n) => (n.textContent = count));
      if (open) this.open();
    }

    async change(line, quantity) {
      this.classList.add('is-busy');
      try {
        const res = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: +line, quantity, sections: 'cart-drawer', sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || theme.strings.error);
        this.renderSections(data.sections, false);
        if (document.body.classList.contains('template-cart')) window.location.reload();
      } catch (e) {
        alert(e.message || theme.strings.error);
      } finally {
        this.classList.remove('is-busy');
      }
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* Cart page quantity buttons reuse the drawer's change() */
  document.addEventListener('click', (e) => {
    const q = e.target.closest('.cart-page [data-qty]');
    const drawer = document.querySelector('cart-drawer');
    if (q && drawer) drawer.change(q.closest('[data-line]').dataset.line, +q.dataset.qty);
  });

  /* Reviews: "Show more" loads the full review file on first click, then reveals 12 at a time. */
  const REVIEWS_PAGE = 12;
  const reviewCard = (rv) => {
    const el = document.createElement('article');
    el.className = 'review';
    const stars = document.createElement('span');
    stars.className = 'stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `Rated ${rv.r} out of 5`);
    stars.textContent = '★'.repeat(rv.r) + '☆'.repeat(5 - rv.r);
    const title = document.createElement('h3');
    title.className = 'review__title';
    title.textContent = `“${rv.t.charAt(0).toUpperCase()}${rv.t.slice(1)}”`;
    const body = document.createElement('p');
    body.textContent = rv.b;
    const by = document.createElement('div');
    by.className = 'review__by';
    const name = document.createElement('span');
    name.textContent = rv.n;
    if (rv.v) {
      const ok = document.createElement('span');
      ok.className = 'review__ok';
      ok.textContent = ' · Verified buyer';
      name.appendChild(ok);
    }
    const meta = document.createElement('span');
    meta.className = 'review__pet';
    meta.textContent = `${rv.c} · ${rv.d}`;
    by.append(name, meta);
    el.append(stars, title, body, by);
    return el;
  };

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-reviews-more]');
    if (!btn) return;
    const list = btn.closest('.sec').querySelector('[data-reviews-list]');
    try {
      if (!btn._reviews) {
        btn.disabled = true;
        const res = await fetch(btn.dataset.src);
        if (!res.ok) throw new Error(res.status);
        btn._reviews = await res.json();
        btn._shown = 0;
      }
      const next = btn._reviews.slice(btn._shown, btn._shown + REVIEWS_PAGE);
      const frag = document.createDocumentFragment();
      next.forEach((rv) => frag.appendChild(reviewCard(rv)));
      list.appendChild(frag);
      btn._shown += next.length;
      const left = btn._reviews.length - btn._shown;
      btn.hidden = left <= 0;
      btn.textContent = `${btn.dataset.label} (${left} more)`;
    } catch (err) {
      btn.textContent = theme.strings.error;
    } finally {
      btn.disabled = false;
    }
  });
})();
